// PEAK Edu Tier 0 WASM — Web Audio capture + AudioWorklet VAD + WS streaming
// Phase 1 Tuần 1 Day 4 skeleton + Sprint Day 44 partial finish (gaps 1+2+3 Tier B-NEW-1)
import { ApiClient, ApiError } from './api-client.js';
import {
  initCookieConsent,
  resetCookieConsent,
  getCookieConsent,
} from './cookie-consent.js';
import { initSentry, captureException } from './sentry-init.js';
import { initPlausible, trackEvent } from './analytics-init.js';
import { ensureAudioConsent, resetAudioConsent } from './audio-consent.js';
import { formatMicError, renderMicErrorToStatus } from './mic-permission.js';

const SAMPLE_RATE = 16000;
const ENGINE_API_BASE =
  import.meta.env?.VITE_ENGINE_API_BASE || 'https://api.peakedu.vn';
const ENGINE_WS_BASE =
  import.meta.env?.VITE_ENGINE_WS_BASE || 'wss://api.peakedu.vn';
// Gap 1 Sprint Day 44 — WP gateway URL (production mode, optional)
// Khi set, browser fetch JWT từ WP plugin endpoint thay direct /session call
// (KHÔNG cần expose bridge secret browser-side).
const WP_GATEWAY_URL =
  import.meta.env?.VITE_WP_GATEWAY_URL || '';

// B17 Sprint Day 44 — Legal/support URL config
// VITE_LEGAL_BASE_URL chỉ tới WordPress site host các page pháp lý.
// Default `https://peakedu.vn` cho production; dev có thể override via .env.
const LEGAL_BASE_URL = (
  import.meta.env?.VITE_LEGAL_BASE_URL || 'https://peakedu.vn'
).replace(/\/$/, '');
const SUPPORT_EMAIL =
  import.meta.env?.VITE_SUPPORT_EMAIL || 'hotro@peakedu.vn';

// B14 Sprint Day 44 — Sentry frontend config (DSN unset = skip silent)
const SENTRY_DSN =
  import.meta.env?.VITE_SENTRY_DSN || '';
const SENTRY_ENVIRONMENT =
  import.meta.env?.VITE_SENTRY_ENVIRONMENT || 'production';
const SENTRY_TRACES_SAMPLE_RATE = Number(
  import.meta.env?.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.1
);
const SENTRY_RELEASE =
  import.meta.env?.VITE_SENTRY_RELEASE || '';

// B29 Sprint Day 44 PM — Plausible Analytics config (privacy-friendly, opt-in)
const PLAUSIBLE_DOMAIN =
  import.meta.env?.VITE_PLAUSIBLE_DOMAIN || '';
const PLAUSIBLE_SCRIPT_SRC =
  import.meta.env?.VITE_PLAUSIBLE_SCRIPT_SRC || '';

const els = {
  btnStart: document.getElementById('btn-start'),
  btnStop: document.getElementById('btn-stop'),
  status: document.getElementById('status'),
  statusText: document.getElementById('status-text'),
  targetText: document.getElementById('target-text'),
  result: document.getElementById('result'),
  resultContent: document.getElementById('result-content'),
  apiEndpoint: document.getElementById('api-endpoint'),
};

els.apiEndpoint.textContent = new URL(ENGINE_API_BASE).host;

// B17 Sprint Day 44 — Wire legal links từ env config
function _wireLegalLinks() {
  const links = {
    'link-privacy': `${LEGAL_BASE_URL}/chinh-sach-bao-mat/`,
    'link-terms': `${LEGAL_BASE_URL}/dieu-khoan-su-dung/`,
    'link-cookie': `${LEGAL_BASE_URL}/chinh-sach-cookie/`,
    'link-support': `mailto:${SUPPORT_EMAIL}`,
  };
  for (const [id, href] of Object.entries(links)) {
    const el = document.getElementById(id);
    if (el) el.setAttribute('href', href);
  }
  // Cookie settings link → reset cookie + audio consent + reload
  const settingsLink = document.getElementById('link-cookie-settings');
  if (settingsLink) {
    settingsLink.addEventListener('click', (e) => {
      e.preventDefault();
      resetCookieConsent();
      resetAudioConsent();  // B+ SD44 PM — reset audio consent too
      window.location.reload();
    });
  }
}
_wireLegalLinks();

// B18+B29 Sprint Day 44 — Cookie consent banner + Plausible Analytics gate
// Chạy sau wire legal links để banner đã có đầy đủ link target.
// onChange callback: load Plausible khi user opt-in analytics.
initCookieConsent({
  legalBaseUrl: LEGAL_BASE_URL,
  onChange: (record) => {
    if (record?.choices?.analytics && PLAUSIBLE_DOMAIN) {
      // B29 — load Plausible script ngay sau user accept analytics
      initPlausible({
        domain: PLAUSIBLE_DOMAIN,
        source: PLAUSIBLE_SCRIPT_SRC || undefined,
      }).then((meta) => {
        if (meta.enabled) {
          // Track first page view event
          trackEvent('app_loaded', { source: 'wasm_tier_0' });
        }
      });
    }
  },
});

// B29 — Auto-init Plausible nếu user đã có consent từ lần truy cập trước
// (cookie banner không show lại vì consent valid <12 tháng)
if (PLAUSIBLE_DOMAIN) {
  initPlausible({
    domain: PLAUSIBLE_DOMAIN,
    source: PLAUSIBLE_SCRIPT_SRC || undefined,
    consentRequired: true,
  }).then((meta) => {
    if (meta.enabled) {
      trackEvent('app_loaded', { source: 'wasm_tier_0' });
    }
    // meta.error: 'no_domain' / 'consent_analytics_off' / 'script_load_fail'
    // Skip silent — fail-open
  });
}

// B14 Sprint Day 44 — Sentry frontend init (DSN gated, fail-open)
// Async init — không block UI. Activate khi boss SD45 set VITE_SENTRY_DSN.
initSentry({
  dsn: SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT,
  tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
  release: SENTRY_RELEASE,
}).then((meta) => {
  if (meta.enabled) {
    // console.debug('[sentry] init ok', meta.version);
  }
});

let state = {
  audioContext: null,
  mediaStream: null,
  workletNode: null,
  apiClient: null,
  voiceActive: false,
  recording: false,
};

function setStatus(kind, text) {
  els.status.className = `status ${kind}`;
  // G1 V7.1 — mic-permission render overwrites innerHTML; rebuild standard structure
  // nếu inner #status-text bị thay thế bởi mic error block.
  let textEl = document.getElementById('status-text');
  if (!textEl) {
    els.status.innerHTML = '<span class="pulse"></span><span id="status-text"></span>';
    textEl = document.getElementById('status-text');
    els.statusText = textEl;
  }
  textEl.textContent = text;
}

/**
 * Gap 3 fix Sprint Day 44 — Score result render với basic color contract.
 *
 * Render strategy:
 *   - Overall score badge với color-coded (≥80 xanh, 60-79 vàng, <60 đỏ)
 *   - Component breakdown (pronunciation/fluency/intonation)
 *   - Phoneme list nếu có color_contract trong payload
 *   - Raw JSON ở cuối cho debug (collapse trong production)
 */
function showResult(payload) {
  els.result.style.display = 'block';

  const overall = _safeNumber(payload?.overall_score);
  const pron = _safeNumber(payload?.pronunciation_score);
  const flu = _safeNumber(payload?.fluency_score);
  const into = _safeNumber(payload?.intonation_score);

  const overallColor = _scoreColor(overall);
  const html = `
    <div class="score-summary" style="display:flex;gap:1rem;margin-bottom:1rem;align-items:center;">
      <div class="score-badge" style="
        font-size:2rem;font-weight:700;
        background:${overallColor};color:white;
        padding:0.75rem 1.25rem;border-radius:8px;
        min-width:80px;text-align:center;
      ">${overall.toFixed(1)}</div>
      <div class="score-label" style="font-size:0.95rem;color:#4a5568;">
        <strong>Điểm tổng / Overall</strong><br>
        <span style="font-size:0.85rem;color:#718096;">Thang 0-100</span>
      </div>
    </div>
    <div class="score-breakdown" style="display:grid;grid-template-columns:repeat(3,1fr);gap:0.5rem;margin-bottom:1rem;">
      ${_componentCell('Phát âm', pron)}
      ${_componentCell('Lưu loát', flu)}
      ${_componentCell('Ngữ điệu', into)}
    </div>
    ${_renderColorContract(payload?.color_contract)}
    <details style="margin-top:1rem;font-size:0.85rem;">
      <summary style="cursor:pointer;color:#4a5568;">Raw JSON (debug)</summary>
      <pre style="background:#f7fafc;padding:0.5rem;border-radius:4px;overflow:auto;max-height:300px;font-size:0.75rem;">${_escapeHtml(JSON.stringify(payload, null, 2))}</pre>
    </details>
  `;
  els.resultContent.innerHTML = html;
}

function _safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function _scoreColor(score) {
  if (score >= 80) return '#22c55e';   // green
  if (score >= 60) return '#eab308';   // yellow
  return '#ef4444';                     // red
}

function _componentCell(label, value) {
  const color = _scoreColor(value);
  return `
    <div style="
      padding:0.5rem;background:#f7fafc;border-radius:4px;
      border-left:3px solid ${color};
    ">
      <div style="font-size:0.75rem;color:#718096;">${label}</div>
      <div style="font-size:1.25rem;font-weight:600;color:${color};">${value.toFixed(1)}</div>
    </div>
  `;
}

function _renderColorContract(cc) {
  if (!cc || typeof cc !== 'object') return '';
  const phonemes = Array.isArray(cc.phonemes) ? cc.phonemes : [];
  if (phonemes.length === 0) return '';
  const items = phonemes.slice(0, 30).map((p) => {
    const sym = _escapeHtml(p.symbol || p.phoneme || '?');
    const score = _safeNumber(p.score_0_100);
    const colorHex = p.color_hex || _scoreColor(score);
    return `<span style="
      display:inline-block;padding:0.25rem 0.5rem;margin:0.125rem;
      background:${colorHex};color:white;border-radius:4px;
      font-size:0.85rem;font-family:monospace;
    " title="${score.toFixed(1)}">${sym}</span>`;
  }).join('');
  return `
    <div style="margin-top:0.5rem;">
      <div style="font-size:0.85rem;color:#4a5568;margin-bottom:0.25rem;">Phoneme breakdown:</div>
      <div>${items}${phonemes.length > 30 ? ' …' : ''}</div>
    </div>
  `;
}

function _escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

/**
 * Gap 2 fix Sprint Day 44 — Render error với distinct kind từ ApiError.
 */
function renderError(err) {
  if (err instanceof ApiError) {
    const kindMap = {
      auth: '🔒 Phiên đăng nhập hết hạn',
      network: '📡 Mất kết nối',
      timeout: '⏱ Quá thời gian chờ',
      server: '⚠ Lỗi máy chủ',
      unknown: '❌ Lỗi',
    };
    setStatus('error', `${kindMap[err.kind] || kindMap.unknown}: ${err.message}`);
  } else {
    setStatus('error', `❌ ${err?.message || 'Lỗi không xác định'}`);
  }
}

async function startRecording() {
  els.btnStart.disabled = true;

  // B+ SD44 PM — explicit audio consent (NĐ 13/2023 Art 26 sinh trắc học)
  // Show modal nếu chưa có valid consent (12-month TTL).
  setStatus('idle', '🔒 Kiểm tra đồng ý ghi âm…');
  const consentOk = await ensureAudioConsent({ legalBaseUrl: LEGAL_BASE_URL });
  if (!consentOk) {
    setStatus('error', '🛑 Bạn đã từ chối ghi âm. Cần đồng ý để sử dụng tính năng chấm điểm.');
    els.btnStart.disabled = false;
    return;
  }

  setStatus('idle', '⏳ Đang xin quyền micro…');

  try {
    state.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: SAMPLE_RATE,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (e) {
    // G1 V7.1 Beta SD70 — Vietnamese mic permission UX với browser-specific guidance
    const formatted = formatMicError(e);
    renderMicErrorToStatus(els.status, formatted);
    captureException?.(e, {
      tags: { source: 'getUserMedia' },
      extra: { errorCode: formatted.errorCode },
    });
    els.btnStart.disabled = false;
    return;
  }

  state.audioContext = new (window.AudioContext || window.webkitAudioContext)({
    sampleRate: SAMPLE_RATE,
  });

  // Load AudioWorklet processor
  try {
    await state.audioContext.audioWorklet.addModule('./audio-worklet.js');
  } catch (e) {
    setStatus('error', `❌ Không load được AudioWorklet: ${e.message}`);
    els.btnStart.disabled = false;
    return;
  }

  state.workletNode = new AudioWorkletNode(state.audioContext, 'vad-processor', {
    processorOptions: { energyThreshold: 500 },
  });

  const source = state.audioContext.createMediaStreamSource(state.mediaStream);
  source.connect(state.workletNode);
  state.workletNode.connect(state.audioContext.destination);

  // Init API client + open session
  // Gap 1 fix Sprint Day 44 — WP gateway mode nếu VITE_WP_GATEWAY_URL set
  state.apiClient = new ApiClient(ENGINE_API_BASE, ENGINE_WS_BASE, {
    wpGatewayUrl: WP_GATEWAY_URL || null,
  });
  setStatus('idle', '⏳ Đang khởi tạo phiên scoring…');

  let session;
  try {
    session = await state.apiClient.createSession(els.targetText.textContent);
  } catch (e) {
    renderError(e);
    cleanup();
    els.btnStart.disabled = false;
    return;
  }

  try {
    await state.apiClient.openWebSocket(session, {
      onReady: () => setStatus('recording', '🎙 Hãy đọc câu — micro đang nghe'),
      onReconnecting: (attempt, delayMs) => {
        setStatus('processing', `🔄 Mất kết nối — đang thử lại lần ${attempt} (${Math.round(delayMs / 1000)}s)…`);
      },
      onReconnected: () => {
        setStatus('recording', '✓ Đã kết nối lại — tiếp tục ghi âm');
      },
      onFinal: (payload) => {
        setStatus('done', '✓ Đã chấm xong');
        showResult(payload);
        cleanup();
      },
      onError: (err) => {
        renderError(err);
        cleanup();
      },
    });
  } catch (e) {
    renderError(e);
    cleanup();
    els.btnStart.disabled = false;
    return;
  }

  state.workletNode.port.onmessage = (event) => {
    const { type, samples } = event.data;
    if (type === 'voice-active' && !state.voiceActive) {
      state.voiceActive = true;
      setStatus('voice-active', '🔊 Đang nghe giọng nói…');
    }
    if (type === 'audio-chunk' && state.apiClient?.isOpen) {
      const pcm16 = float32ToPcm16(samples);
      state.apiClient.sendAudio(pcm16);
    }
  };

  state.recording = true;
  els.btnStop.disabled = false;
  els.btnStart.disabled = true;
}

function stopRecording() {
  setStatus('processing', '⏳ Đang chờ kết quả từ Engine…');
  els.btnStop.disabled = true;
  if (state.workletNode) {
    state.workletNode.port.postMessage({ type: 'flush' });
  }
}

function cleanup() {
  if (state.workletNode) state.workletNode.disconnect();
  if (state.audioContext) state.audioContext.close();
  if (state.mediaStream) state.mediaStream.getTracks().forEach((t) => t.stop());
  if (state.apiClient) state.apiClient.close();
  state = { audioContext: null, mediaStream: null, workletNode: null, apiClient: null, voiceActive: false, recording: false };
  els.btnStart.disabled = false;
  els.btnStop.disabled = true;
}

function float32ToPcm16(float32) {
  const buf = new ArrayBuffer(float32.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < float32.length; i++) {
    let s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

els.btnStart.addEventListener('click', startRecording);
els.btnStop.addEventListener('click', stopRecording);

setStatus('idle', '✓ Sẵn sàng — bấm "Bắt đầu ghi âm" để cấp quyền micro');
